import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/useCart';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { RegisterForm, RegisterData } from './RegisterForm';

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const LoginDialog = ({ open, onOpenChange, onSuccess }: LoginDialogProps) => {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [registerErrors, setRegisterErrors] = useState<Record<string, string[]> | undefined>();
  const { requestOTP, verifyOTP, register, isLoading, error, isAuthenticated } = useAuth();
  const { refreshCart } = useCart();

  // Don't render if user is already authenticated
  if (isAuthenticated) {
    return null;
  }

  const handleSendOTP = async () => {
    if (phoneNumber.length === 10) {
      try {
        await requestOTP(phoneNumber);
        setStep('otp');
        toast.success('OTP sent successfully', {
          description: `OTP has been sent to +91 ${phoneNumber}`,
        });
      } catch (err) {
        toast.error('Failed to send OTP', {
          description: err instanceof Error ? err.message : 'Please try again',
        });
      }
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length === 6) {
      try {
        await verifyOTP(phoneNumber, otp);
        // Refresh cart to merge guest cart with logged-in user account
        await refreshCart();
        onOpenChange(false);
        toast.success('Login successful!');
        setStep('phone');
        setPhoneNumber('');
        setOtp('');
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }
      } catch (err) {
        toast.error('Invalid OTP', {
          description: 'Please check your OTP and try again',
        });
      }
    }
  };

  const handleResendOTP = async () => {
    try {
      await requestOTP(phoneNumber);
      toast.success('OTP resent successfully');
    } catch (err) {
      toast.error('Failed to resend OTP', {
        description: err instanceof Error ? err.message : 'Please try again',
      });
    }
  };

  const handleRegister = async (data: RegisterData) => {
    try {
      setRegisterErrors(undefined);
      await register(data);
      // Refresh cart to merge guest cart with new user account
      await refreshCart();
      onOpenChange(false);
      toast.success('Registration successful!', {
        description: 'Welcome to Indispare!',
      });
      // Reset form
      setActiveTab('login');
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      // Handle validation errors
      if (err.errors) {
        setRegisterErrors(err.errors);
        toast.error('Registration failed', {
          description: 'Please check the form and try again',
        });
      } else {
        toast.error('Registration failed', {
          description: err.message || 'Please try again',
        });
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>
            <div className="text-center">
              <div className="flex justify-center mb-2">
                <img 
                  src="/logoFinal.svg" 
                  alt="Indispare Logo" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-sm text-muted-foreground">Countless Parts, Happy Hearts!</p>
            </div>
          </SheetTitle>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'login' | 'register')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="register">Register</TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="mt-4">
            {step === 'phone' ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-center mb-2">Log in or sign up</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    You can send the OTP by selecting below options to Login
                  </p>
                </div>

                <div className="flex gap-2">
                  <div className="flex items-center justify-center px-3 py-2 border rounded-md bg-muted">
                    <span className="font-medium">+91</span>
                  </div>
                  <Input
                    placeholder="Enter Phone number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="flex-1"
                  />
                </div>

                <p className="text-xs text-muted-foreground text-center">
                  You'll receive a 6-digit OTP though SMS
                </p>

                <Button onClick={handleSendOTP} className="w-full" disabled={phoneNumber.length !== 10 || isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  By continuing, you agree to our Terms of Service & Privacy Policy
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-center mb-2">OTP Verification</h3>
                  <p className="text-sm text-muted-foreground text-center">
                    OTP has been sent to +91 {phoneNumber}
                  </p>
                </div>

                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <p className="text-sm text-muted-foreground text-center">
                  You're in! Getting things ready...
                </p>

                <button
                  onClick={handleResendOTP}
                  disabled={isLoading}
                  className="text-sm text-primary text-center w-full hover:underline disabled:opacity-50"
                >
                  {isLoading ? 'Resending...' : 'Resend OTP'}
                </button>

                <p className="text-xs text-center text-muted-foreground">
                  By continuing, you agree to our Terms of Service & Privacy Policy
                </p>

                <Button onClick={handleVerifyOTP} className="w-full" disabled={otp.length !== 6 || isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify OTP'
                  )}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="register" className="mt-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-center mb-2">Create New Account</h3>
                <p className="text-sm text-muted-foreground text-center">
                  Fill in your details to get started
                </p>
              </div>

              <RegisterForm onRegister={handleRegister} isLoading={isLoading} errors={registerErrors} />

              <p className="text-xs text-center text-muted-foreground">
                By registering, you agree to our Terms of Service & Privacy Policy
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
